/**
 * Master-Findings-Editor: kanonischer Findings-Bestand (DB-only, kein Obsidian).
 *
 * Linke Spalte: Liste aller Findings, gefiltert nach Status / Stichwort.
 * Rechte Spalte: Detail des ausgewaehlten Findings — Original, Fix-Versionen,
 *   Joerg-Kommentare, Test-Pfade — alle mit Add/Delete-Knoepfen.
 *
 * Joerg legt neue Funde an, ergaenzt Kommentare nach jeder Tester-Welle, ich
 * (Claude) schreibe `Fix vN+1` direkt ueber API. Keine Vault-Roundtrips mehr.
 *
 * Dokument-Navigation Scheibe 3 (Abhakliste 3.60, 2026-10-01): keine Browser-Hinweisfenster
 * mehr — Rückfragen/Meldungen/Texteingaben über `useDialogs()`, mehrfeldrige Eingaben
 * (neuer Fund, Fix-Version, Test-Pfad) als Dialog mit Dialog-Schutz (`GuardedModal`).
 */
import { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Trash2, Search, FileText, GitCommit, MessageSquare, Link as LinkIcon, RefreshCw } from 'lucide-react';
import { GuardedModal, useDialogs } from '@onvice/portal-ui';
import { testCockpitApi, type MasterFinding } from '../../../lib/testCockpitApi';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  'offen':           { label: 'offen',           cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
  'in Verfeinerung': { label: 'in Verfeinerung', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  'final':           { label: 'final',           cls: 'bg-brand-500/15 text-brand-700 dark:text-brand-300' },
  'obsolete':        { label: 'obsolete',        cls: 'bg-slate-500/15 text-slate-500' },
};

const INPUT = 'w-full px-3 py-2 rounded bg-background border border-default text-sm';
const BTN_GHOST = 'px-3 py-2 rounded text-sm hover:bg-primary/5 min-h-[44px]';
const BTN_PRIMARY = 'px-4 py-2 rounded bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 min-h-[44px]';

export default function MasterFindingsEditor() {
  const dlg = useDialogs();
  const [findings, setFindings] = useState<MasterFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    testCockpitApi.listMasterFindings()
      .then(fs => {
        setFindings(fs);
        if (selectedId && !fs.some(f => f.id === selectedId)) setSelectedId(null);
      })
      .catch((e: unknown) => { setFindings([]); setLoadError((e as Error).message || 'Laden fehlgeschlagen'); })
      .finally(() => setLoading(false));
  }, [selectedId]);

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = findings.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!f.title.toLowerCase().includes(s) && !f.source_label.toLowerCase().includes(s) && !(f.original_text ?? '').toLowerCase().includes(s)) return false;
    }
    return true;
  });
  const selected = findings.find(f => f.id === selectedId) ?? null;

  async function importVault() {
    const ok = await dlg.confirm({
      title: 'Findings aus dem Vault importieren?',
      message: 'Bestehende Funde werden ergänzt, nicht überschrieben. Im nächsten Schritt kannst du wählen, ob Fix-Versionen, Kommentare und Test-Pfade frisch reimportiert werden.',
      confirmLabel: 'Weiter',
    });
    if (!ok) return;
    const overwrite = await dlg.confirm({
      title: 'Mit Überschreiben importieren?',
      message: 'Ja: alte Fix-Versionen, Kommentare und Test-Pfade werden gelöscht und neu geschrieben. Nein: nur neue Einträge ergänzen.',
      confirmLabel: 'Ja, überschreiben',
      cancelLabel: 'Nein, nur ergänzen',
      danger: true,
    });
    setBusy(true);
    try {
      const r = await testCockpitApi.importVault(overwrite);
      await dlg.notify({ kind: 'success', title: 'Import abgeschlossen', message: `${r.findings} Findings · ${r.fix_versions} Fix-Versionen · ${r.admin_comments} Kommentare · ${r.test_paths} Test-Pfade` });
      reload();
    } catch (e) { await dlg.error('Import fehlgeschlagen', e); }
    finally { setBusy(false); }
  }

  async function createFinding(input: { sourceLabel: string; title: string; original: string }) {
    // Naechster idx innerhalb der source_label
    const sameSource = findings.filter(f => f.source_label === input.sourceLabel);
    const nextIdx = sameSource.reduce((max, f) => Math.max(max, f.idx + 1), 0);
    const created = await testCockpitApi.createMasterFinding({
      source_label: input.sourceLabel,
      idx: nextIdx,
      title: input.title,
      original_text: input.original,
      original_date: new Date().toISOString().slice(0, 10),
      status: 'offen',
    });
    reload();
    setSelectedId(created.id);
  }

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 size={14} className="animate-spin" /> Findings laden …</div>;
  }

  return (
    <div className="bg-card border border-default rounded-lg flex flex-col" style={{ minHeight: '700px' }} data-test-anchor="master-editor">
      <div className="px-4 py-3 border-b border-default flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-brand-500" />
          <span className="font-medium">Findings (Master)</span>
          <span className="text-xs text-muted-foreground">{filtered.length} / {findings.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowNew(true)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-primary text-white hover:bg-primary/90 disabled:opacity-50" data-action="new">
            <Plus size={14} /> Neu
          </button>
          <button type="button" onClick={importVault} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-card border border-default hover:bg-primary/5 disabled:opacity-50" title="Aus vault_files re-importieren">
            <RefreshCw size={14} /> Vault-Import
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mx-4 mt-3 text-sm text-rose-500 bg-rose-500/10 border border-rose-500/30 rounded p-3 flex items-center justify-between gap-2" data-test-anchor="load-error">
          <span>Nicht geladen: {loadError}</span>
          <button type="button" onClick={reload} className="text-xs px-2 py-1 rounded bg-card border border-default hover:bg-primary/5">Erneut laden</button>
        </div>
      )}

      <div className="px-4 py-2 border-b border-default flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={12} className="absolute left-2 top-2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Suche Titel / Original / Quelle …"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-7 pr-2 py-1.5 rounded bg-background border border-default text-sm w-full"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1.5 rounded bg-background border border-default text-sm">
          <option value="all">alle Status</option>
          <option value="offen">offen</option>
          <option value="in Verfeinerung">in Verfeinerung</option>
          <option value="final">final</option>
          <option value="obsolete">obsolete</option>
        </select>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Liste */}
        <div className="w-1/3 border-r border-default overflow-y-auto" style={{ maxHeight: '650px' }} data-test-anchor="master-list">
          {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground italic">Keine Findings.</div>}
          {filtered.map(f => {
            const badge = STATUS_LABEL[f.status] ?? STATUS_LABEL['offen'];
            const isSel = f.id === selectedId;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setSelectedId(f.id)}
                className={`block w-full text-left px-3 py-2 border-b border-default/50 hover:bg-primary/5 ${isSel ? 'bg-brand-500/10' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${badge.cls}`}>{badge.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{f.source_label} · #{f.idx + 1} · {f.fix_versions.length} Fix(es)</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: '650px' }}>
          {!selected ? (
            <div className="text-sm text-muted-foreground italic">Finding aus der Liste wählen — oder mit „Neu“ anlegen.</div>
          ) : (
            <FindingDetail finding={selected} onChanged={reload} />
          )}
        </div>
      </div>

      <NewFindingModal open={showNew} onClose={() => setShowNew(false)} onCreate={createFinding} />
    </div>
  );
}

/** Neuer Fund: drei Felder in einem Dialog mit Dialog-Schutz (vorher drei Browser-Prompts). */
function NewFindingModal({ open, onClose, onCreate }: { open: boolean; onClose: () => void; onCreate: (input: { sourceLabel: string; title: string; original: string }) => Promise<void> }) {
  const dlg = useDialogs();
  const [sourceLabel, setSourceLabel] = useState('');
  const [title, setTitle] = useState('');
  const [original, setOriginal] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = sourceLabel.trim() !== '' || title.trim() !== '' || original.trim() !== '';

  function reset() { setSourceLabel(''); setTitle(''); setOriginal(''); }

  async function submit() {
    const issues: string[] = [];
    if (!sourceLabel.trim()) issues.push('Quelle (Welle / Datei) fehlt');
    if (!title.trim()) issues.push('Titel fehlt');
    if (issues.length) { await dlg.notify({ kind: 'error', title: 'Fund kann nicht angelegt werden', message: 'Bitte zuerst ergänzen:', items: issues }); return; }
    setBusy(true);
    try {
      await onCreate({ sourceLabel: sourceLabel.trim(), title: title.trim(), original: original.trim() });
      reset();
      onClose();
    } catch (e) { await dlg.error('Anlegen fehlgeschlagen', e); }
    finally { setBusy(false); }
  }

  return (
    <GuardedModal open={open} title="Neuen Fund anlegen" label="Fund" dirty={dirty} busy={busy} onClose={() => { reset(); onClose(); }} size="md" testAnchor="finding-new"
      footer={(requestClose) => (
        <>
          <button type="button" onClick={requestClose} className={BTN_GHOST}>Abbrechen</button>
          <button type="button" onClick={submit} disabled={busy} className={BTN_PRIMARY}>Anlegen</button>
        </>
      )}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Quelle (Welle / Datei), z. B. 2026-05-05-runde-3</span>
          <input value={sourceLabel} onChange={e => setSourceLabel(e.target.value)} className={INPUT} data-test-anchor="finding-source" />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Titel des Funds</span>
          <input value={title} onChange={e => setTitle(e.target.value)} className={INPUT} data-test-anchor="finding-title" />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Original-Wortlaut (Jörgs Beschreibung)</span>
          <textarea value={original} onChange={e => setOriginal(e.target.value)} rows={4} className={INPUT} />
        </label>
      </div>
    </GuardedModal>
  );
}

function FindingDetail({ finding, onChanged }: { finding: MasterFinding; onChanged: () => void }) {
  const dlg = useDialogs();
  const [busy, setBusy] = useState(false);
  const [showFix, setShowFix] = useState(false);
  const [showPath, setShowPath] = useState(false);

  async function setStatus(status: MasterFinding['status']) {
    setBusy(true);
    try { await testCockpitApi.updateMasterFinding(finding.id, { status }); onChanged(); }
    catch (e) { await dlg.error('Status wurde nicht gespeichert', e); }
    finally { setBusy(false); }
  }

  async function editOriginal() {
    const t = await dlg.promptText({ title: 'Original-Wortlaut Jörg', initialValue: finding.original_text ?? '', multiline: true, allowEmpty: true, confirmLabel: 'Speichern' });
    if (t === null) return;
    setBusy(true);
    try { await testCockpitApi.updateMasterFinding(finding.id, { original_text: t }); onChanged(); }
    catch (e) { await dlg.error('Original wurde nicht gespeichert', e); }
    finally { setBusy(false); }
  }
  async function editTitle() {
    const t = await dlg.promptText({ title: 'Titel des Funds', initialValue: finding.title, confirmLabel: 'Speichern' });
    if (!t) return;
    setBusy(true);
    try { await testCockpitApi.updateMasterFinding(finding.id, { title: t }); onChanged(); }
    catch (e) { await dlg.error('Titel wurde nicht gespeichert', e); }
    finally { setBusy(false); }
  }

  async function addFix(input: { text: string; commit: string }) {
    await testCockpitApi.addFixVersion(finding.id, {
      fix_text: input.text,
      commit_hash: input.commit || undefined,
      fix_date: new Date().toISOString().slice(0, 10),
    });
    onChanged();
  }

  async function addComment() {
    const text = await dlg.promptText({ title: 'Jörg-Kommentar', fieldLabel: 'Kommentar', multiline: true, confirmLabel: 'Hinzufügen' });
    if (!text) return;
    setBusy(true);
    try {
      await testCockpitApi.addAdminComment(finding.id, {
        comment_text: text,
        comment_date: new Date().toISOString().slice(0, 10),
        sort_order: finding.admin_comments.length,
      });
      onChanged();
    } catch (e) { await dlg.error('Kommentar wurde nicht gespeichert', e); }
    finally { setBusy(false); }
  }

  async function addTestPath(input: { persona: string; url: string; hint: string; expectation: string }) {
    await testCockpitApi.addTestPath(finding.id, {
      persona_key: input.persona || undefined,
      url_path: input.url,
      hint: input.hint || undefined,
      expectation: input.expectation || undefined,
      sort_order: finding.test_paths.length,
    });
    onChanged();
  }

  async function deleteFix(version: number) {
    // Backend identifiziert via finding-fix-versions/:id — die Liste liefert nur die Versionsnummer.
    await dlg.notify({ kind: 'info', title: `Fix v${version}`, message: 'Einzelne Fix-Versionen lassen sich hier noch nicht löschen — bitte über die Datenbank oder den Vault-Import mit Überschreiben.' });
  }
  async function deleteComment(id: string) {
    if (!(await dlg.confirm({ title: 'Kommentar löschen?', message: 'Der Kommentar wird endgültig entfernt.', confirmLabel: 'Löschen', danger: true }))) return;
    setBusy(true);
    try { await testCockpitApi.deleteAdminComment(id); onChanged(); }
    catch (e) { await dlg.error('Kommentar wurde nicht gelöscht', e); }
    finally { setBusy(false); }
  }
  async function deleteTestPath(id: string) {
    if (!(await dlg.confirm({ title: 'Test-Pfad löschen?', message: 'Der Test-Pfad wird endgültig entfernt.', confirmLabel: 'Löschen', danger: true }))) return;
    setBusy(true);
    try { await testCockpitApi.deleteTestPath(id); onChanged(); }
    catch (e) { await dlg.error('Test-Pfad wurde nicht gelöscht', e); }
    finally { setBusy(false); }
  }

  const badge = STATUS_LABEL[finding.status] ?? STATUS_LABEL['offen'];
  return (
    <div className="space-y-4" data-test-anchor="finding-detail">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">{finding.source_label} · #{finding.idx + 1}</div>
          <button type="button" onClick={editTitle} disabled={busy} className="text-lg font-semibold text-left hover:underline" title="Titel ändern">{finding.title}</button>
        </div>
        <div className="flex items-center gap-1">
          {(['offen', 'in Verfeinerung', 'final', 'obsolete'] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              disabled={busy || finding.status === s}
              className={`text-xs px-2 py-1 rounded ${finding.status === s ? badge.cls + ' ring-2 ring-offset-1 ring-current' : 'border border-default text-muted-foreground hover:text-foreground'}`}
            >
              {STATUS_LABEL[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* Original */}
      <div className="border border-default rounded p-3 bg-background/40">
        <div className="text-xs text-muted-foreground mb-1 flex items-center justify-between">
          <span>Original Jörg{finding.original_date ? ` [${finding.original_date.slice(0, 10)}]` : ''}</span>
          <button type="button" onClick={editOriginal} disabled={busy} className="text-xs hover:text-foreground">bearbeiten</button>
        </div>
        <div className="text-sm whitespace-pre-wrap">{finding.original_text || <span className="italic text-muted-foreground">(leer)</span>}</div>
      </div>

      {/* Fix-Versionen */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><GitCommit size={12} /> Fix-Versionen</div>
          <button type="button" onClick={() => setShowFix(true)} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus size={10} /> Fix vN+1
          </button>
        </div>
        <div className="space-y-2">
          {finding.fix_versions.length === 0 && <div className="text-sm text-muted-foreground italic">Noch kein Fix.</div>}
          {finding.fix_versions.map(fv => (
            <div key={fv.version} className="border border-default rounded p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>v{fv.version} {fv.fix_date ? `· ${fv.fix_date.slice(0, 10)}` : ''} {fv.commit_hash ? `· ${fv.commit_hash}` : ''}</span>
                <button type="button" onClick={() => deleteFix(fv.version)} disabled={busy} className="hover:text-rose-500" title="Fix-Version löschen"><Trash2 size={11} /></button>
              </div>
              <div className="whitespace-pre-wrap">{fv.fix_text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Joerg-Kommentare */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquare size={12} /> Jörg-Kommentare</div>
          <button type="button" onClick={addComment} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus size={10} /> Kommentar
          </button>
        </div>
        <div className="space-y-2">
          {finding.admin_comments.length === 0 && <div className="text-sm text-muted-foreground italic">Keine Kommentare.</div>}
          {[...finding.admin_comments].sort((a, b) => a.sort_order - b.sort_order).map(c => (
            <div key={c.id} className="border border-default rounded p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{c.comment_date ? c.comment_date.slice(0, 10) : '—'}</span>
                <button type="button" onClick={() => deleteComment(c.id)} disabled={busy} className="hover:text-rose-500" title="Kommentar löschen"><Trash2 size={11} /></button>
              </div>
              <div className="whitespace-pre-wrap">{c.comment_text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Test-Pfade */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><LinkIcon size={12} /> Test-Pfade</div>
          <button type="button" onClick={() => setShowPath(true)} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20">
            <Plus size={10} /> Pfad
          </button>
        </div>
        <div className="space-y-2">
          {finding.test_paths.length === 0 && <div className="text-sm text-muted-foreground italic">Kein Test-Pfad.</div>}
          {[...finding.test_paths].sort((a, b) => a.sort_order - b.sort_order).map(tp => (
            <div key={tp.id} className="border border-default rounded p-2 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>{tp.persona_key ? `tester-${tp.persona_key}` : '(beliebige Persona)'} · {tp.stage}</span>
                <button type="button" onClick={() => deleteTestPath(tp.id)} disabled={busy} className="hover:text-rose-500" title="Test-Pfad löschen"><Trash2 size={11} /></button>
              </div>
              <div className="font-mono text-xs">{tp.url_path}</div>
              {tp.hint && <div className="text-xs text-muted-foreground mt-1">{tp.hint}</div>}
              {tp.expectation && <div className="text-xs mt-1"><strong>Erwartung:</strong> {tp.expectation}</div>}
            </div>
          ))}
        </div>
      </div>

      <FixModal open={showFix} onClose={() => setShowFix(false)} onSave={addFix} />
      <TestPathModal open={showPath} onClose={() => setShowPath(false)} onSave={addTestPath} />
    </div>
  );
}

/** Fix vN+1: Beschreibung + Commit in einem Dialog (vorher zwei Browser-Prompts). */
function FixModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (input: { text: string; commit: string }) => Promise<void> }) {
  const dlg = useDialogs();
  const [text, setText] = useState('');
  const [commit, setCommit] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = text.trim() !== '' || commit.trim() !== '';
  function reset() { setText(''); setCommit(''); }
  async function submit() {
    if (!text.trim()) { await dlg.notify({ kind: 'error', title: 'Fix-Beschreibung fehlt', message: 'Bitte beschreiben, was geändert wurde.' }); return; }
    setBusy(true);
    try { await onSave({ text: text.trim(), commit: commit.trim() }); reset(); onClose(); }
    catch (e) { await dlg.error('Fix-Version wurde nicht gespeichert', e); }
    finally { setBusy(false); }
  }
  return (
    <GuardedModal open={open} title="Fix-Version hinzufügen" label="Fix-Version" dirty={dirty} busy={busy} onClose={() => { reset(); onClose(); }} size="md" testAnchor="fix-new"
      footer={(requestClose) => (
        <>
          <button type="button" onClick={requestClose} className={BTN_GHOST}>Abbrechen</button>
          <button type="button" onClick={submit} disabled={busy} className={BTN_PRIMARY}>Hinzufügen</button>
        </>
      )}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Fix-Beschreibung</span>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4} className={INPUT} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Commit-Hash (optional)</span>
          <input value={commit} onChange={e => setCommit(e.target.value)} className={INPUT} />
        </label>
      </div>
    </GuardedModal>
  );
}

/** Test-Pfad: Persona, Pfad, Hinweis, Erwartung in einem Dialog (vorher vier Browser-Prompts). */
function TestPathModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (input: { persona: string; url: string; hint: string; expectation: string }) => Promise<void> }) {
  const dlg = useDialogs();
  const [persona, setPersona] = useState('handwerker');
  const [url, setUrl] = useState('');
  const [hint, setHint] = useState('');
  const [expectation, setExpectation] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = url.trim() !== '' || hint.trim() !== '' || expectation.trim() !== '';
  function reset() { setPersona('handwerker'); setUrl(''); setHint(''); setExpectation(''); }
  async function submit() {
    const issues: string[] = [];
    if (!url.trim().startsWith('/')) issues.push('Pfad muss mit „/“ beginnen, z. B. /m/bauberichteV2/tasks/<id>?scrollTo=timer');
    if (issues.length) { await dlg.notify({ kind: 'error', title: 'Test-Pfad unvollständig', message: 'Bitte zuerst ergänzen:', items: issues }); return; }
    setBusy(true);
    try { await onSave({ persona, url: url.trim(), hint: hint.trim(), expectation: expectation.trim() }); reset(); onClose(); }
    catch (e) { await dlg.error('Test-Pfad wurde nicht gespeichert', e); }
    finally { setBusy(false); }
  }
  return (
    <GuardedModal open={open} title="Test-Pfad hinzufügen" label="Test-Pfad" dirty={dirty} busy={busy} onClose={() => { reset(); onClose(); }} size="md" testAnchor="testpath-new"
      footer={(requestClose) => (
        <>
          <button type="button" onClick={requestClose} className={BTN_GHOST}>Abbrechen</button>
          <button type="button" onClick={submit} disabled={busy} className={BTN_PRIMARY}>Hinzufügen</button>
        </>
      )}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Persona</span>
          <select value={persona} onChange={e => setPersona(e.target.value)} className={INPUT}>
            <option value="handwerker">handwerker</option>
            <option value="gf">gf</option>
            <option value="admin">admin</option>
            <option value="">(beliebig)</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">URL-Pfad, z. B. /m/bauberichteV2/tasks/&lt;id&gt;?scrollTo=timer</span>
          <input value={url} onChange={e => setUrl(e.target.value)} className={`${INPUT} font-mono`} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Hinweis für Tester (optional)</span>
          <textarea value={hint} onChange={e => setHint(e.target.value)} rows={2} className={INPUT} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-muted-foreground">Erwartetes Verhalten (optional)</span>
          <textarea value={expectation} onChange={e => setExpectation(e.target.value)} rows={2} className={INPUT} />
        </label>
      </div>
    </GuardedModal>
  );
}
